/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import UpdateableLabelComponent from './UpdateableLabelComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Classes used to resize labels based on observable value. */
@cc._decorator.ccclass
@cc._decorator.requireComponent(UpdateableLabelComponent)
@cc._decorator.executionOrder(1)
export default class AutoResizeLabelComponent extends ComponentBase
{
    /** After how many digits to begin scaling. */
    @property(cc.Integer)
    protected beginScalingStringLength: number = 9;

    /** After start how many digits to scale again. */
    @property(cc.Integer)
    protected scaleEvery: number = 3;

    /** Percent of scale to amount. */
    @property(cc.Float)
    protected fontScale: number = .15;

    /** Max possible scale reduction. */
    public static readonly MaxScaleAmount: number = 8;
    /** Min size font can be reducd to. */
    public static readonly MinFontSize: number = .01;

    /** Limit value and corresponding font size. */
    protected stringLengthFontLimits: [number, number][] = [];
    /** Original label font size. */
    protected startingFontSize: number = 0;
    /** Last set font size. */
    protected lastFontSize: number = 0;
    /** String length when last set. */
    protected lastStringLength: number = 0;

    /** Sibling component. */
    protected updateableLabel: UpdateableLabelComponent = null;

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.configureDependencies();
        this.calculateScaleLimits();

        this.stringLengthChangedHandler(this.updateableLabel.StringLengthObservable.Value);
    }

    /** @hidden */
    protected onDestroy(): void
    {
        if (this.updateableLabel && this.updateableLabel.StringLengthObservable)
        {
            this.updateableLabel.StringLengthObservable.OffChanged(
                (event: cc.Event.EventCustom) => this.stringLengthChangedHandler(event.detail));
        }
    }

    /** Configure dependent components. */
    private configureDependencies = (): void =>
    {
        this.updateableLabel = this.getComponent(UpdateableLabelComponent);

        if (!this.updateableLabel)
        {
            CLog.error('No UpdateableLabelComponent configured.', this, LogTypes.Initialization);
            this.enabled = false;
        }

        this.updateableLabel.StringLengthObservable.OnChanged(
            (event: cc.Event.EventCustom) => this.stringLengthChangedHandler(event.detail));

        this.startingFontSize = this.updateableLabel.Component.fontSize;
    }

    /** Sets range limits at which a number will need to scale. */
    private calculateScaleLimits = (): void =>
    {
        let fontReduction: number = this.startingFontSize * this.fontScale;

        // Fill limits with matching fontSize.
        for (let i: number = 0; i <= AutoResizeLabelComponent.MaxScaleAmount; i++)
        {
            let amount: number = this.beginScalingStringLength + (this.scaleEvery * i);
            let fontSize: number = this.startingFontSize - (i * fontReduction);

            if (fontSize > AutoResizeLabelComponent.MinFontSize)
            {
                this.stringLengthFontLimits.push([amount, fontSize]);
            }
            else
            {
                this.stringLengthFontLimits.push([amount, AutoResizeLabelComponent.MinFontSize]);
                return;
            }
        }
    }

    /** Handler for value changed on observable. */
    private stringLengthChangedHandler = (length: number): void =>
    {
        this.scaleFontSize(length);
    }

    /**
     * Sets a font-size for givent value based on scale.
     * @param stringLength Length of label's current string value.
     */
    private scaleFontSize = (stringLength: number): void =>
    {
        if (stringLength === this.lastStringLength) { return; }

        for (let stringLengthFontLimit of this.stringLengthFontLimits)
        {
            // Not below this limit, keep looking.
            if (stringLength >= stringLengthFontLimit[0]) { continue; }

            // Minimum value limit font size.
            this.lastStringLength = stringLength;
            this.setFontSize(stringLengthFontLimit[1]);
            return;
        }

        this.setFontSize(this.startingFontSize);
    }

    /** Sets fontSize of label and prevents re-setting. */
    private setFontSize = (fontSize: number): void =>
    {
        if (this.lastFontSize === fontSize) { return; }

        this.updateableLabel.Component.fontSize = fontSize;
        this.lastFontSize = fontSize;
    }
}
